import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import swal from "sweetalert";
import api from "../../../../api";

const ReimbursementFormModal = ({ show, onHide, claim, onSuccess }) => {
  const [formData, setFormData] = useState({
    category: "",
    amount: "",
    expenseDate: new Date().toISOString().split("T")[0],
    merchant: "",
    project: "",
    workOrder: "",
    description: "",
  });

  const [projects, setProjects] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      loadProjectsAndWorkOrders();
      if (claim) {
        setFormData({
          category: claim.category || "",
          amount: claim.amount || "",
          expenseDate: claim.expenseDate
            ? new Date(claim.expenseDate).toISOString().split("T")[0]
            : new Date().toISOString().split("T")[0],
          merchant: claim.merchant || "",
          project: claim.project?._id || claim.project || "",
          workOrder: claim.workOrder?._id || claim.workOrder || "",
          description: claim.description || "",
        });
        setSelectedFiles([]);
        setFilePreviews([]);
      } else {
        setFormData({
          category: "",
          amount: "",
          expenseDate: new Date().toISOString().split("T")[0],
          merchant: "",
          project: "",
          workOrder: "",
          description: "",
        });
        setSelectedFiles([]);
        setFilePreviews([]);
      }
    }
  }, [show, claim]);

  const loadProjectsAndWorkOrders = async () => {
    try {
      const [projRes, woRes] = await Promise.allSettled([
        api.get("/projects?limit=500"),
        api.get("/work-orders?limit=500"),
      ]);

      if (projRes.status === "fulfilled" && projRes.value.data.success) {
        setProjects(projRes.value.data.data || []);
      }
      if (woRes.status === "fulfilled" && woRes.value.data.success) {
        setWorkOrders(woRes.value.data.data || []);
      }
    } catch (err) {
      console.warn("Failed to load projects/work orders:", err);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles((prev) => [...prev, ...files]);

    const newPreviews = files.map((file) => ({
      name: file.name,
      size: (file.size / 1024).toFixed(1) + " KB",
      isImage: file.type.startsWith("image/"),
      url: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
    }));

    setFilePreviews((prev) => [...prev, ...newPreviews]);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setFilePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category || !formData.category.trim()) {
      swal("Warning", "Please enter an expense category", "warning");
      return;
    }

    if (!formData.amount || Number(formData.amount) <= 0) {
      swal("Warning", "Please enter a valid expense amount", "warning");
      return;
    }

    try {
      setSaving(true);
      const data = new FormData();
      data.append("category", formData.category.trim());
      data.append("amount", formData.amount);
      data.append("expenseDate", formData.expenseDate);
      data.append("merchant", formData.merchant);
      if (formData.project) data.append("project", formData.project);
      if (formData.workOrder) data.append("workOrder", formData.workOrder);
      data.append("description", formData.description);

      selectedFiles.forEach((file) => {
        data.append("receipts", file);
      });

      let res;
      if (claim?._id) {
        res = await api.put(`/employee-reimbursements/${claim._id}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await api.post("/employee-reimbursements", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      if (res.data.success) {
        swal("Success", claim?._id ? "Claim updated successfully" : "Expense claim submitted successfully", "success");
        onSuccess && onSuccess();
        onHide();
      } else {
        swal("Error", res.data.message || "Failed to submit claim", "error");
      }
    } catch (err) {
      swal("Error", err.response?.data?.message || err.message || "Failed to submit claim", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 17, fontWeight: 700 }}>
            {claim ? "Edit Reimbursement Claim" : "Raise New Expense Reimbursement"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: "calc(100vh - 180px)", overflowY: "auto" }}>
          <Row className="g-3">
            {/* Category (Pure Manual Input) */}
            <Col md={6}>
              <Form.Label className="fw-semibold small text-muted">EXPENSE CATEGORY *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Travel, Fuel, Food, Site Tools, Courier..."
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
              />
            </Col>

            {/* Amount */}
            <Col md={6}>
              <Form.Label className="fw-semibold small text-muted">CLAIM AMOUNT (₹) *</Form.Label>
              <div className="input-group">
                <span className="input-group-text bg-light fw-bold text-success">₹</span>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="1"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                />
              </div>
            </Col>

            {/* Expense Date */}
            <Col md={6}>
              <Form.Label className="fw-semibold small text-muted">EXPENSE DATE *</Form.Label>
              <Form.Control
                type="date"
                value={formData.expenseDate}
                onChange={(e) => setFormData({ ...formData, expenseDate: e.target.value })}
                required
              />
            </Col>

            {/* Merchant / Vendor */}
            <Col md={6}>
              <Form.Label className="fw-semibold small text-muted">MERCHANT / PAID TO</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Indian Oil Petrol Pump, Uber, Hardware Store"
                value={formData.merchant}
                onChange={(e) => setFormData({ ...formData, merchant: e.target.value })}
              />
            </Col>

            {/* Project Tagging */}
            <Col md={6}>
              <Form.Label className="fw-semibold small text-muted">PROJECT (OPTIONAL)</Form.Label>
              <Form.Select
                value={formData.project}
                onChange={(e) => setFormData({ ...formData, project: e.target.value })}
              >
                <option value="">-- General / Non-Project --</option>
                {projects.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.projectName || p.name}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* Work Order Tagging */}
            <Col md={6}>
              <Form.Label className="fw-semibold small text-muted">WORK ORDER (OPTIONAL)</Form.Label>
              <Form.Select
                value={formData.workOrder}
                onChange={(e) => setFormData({ ...formData, workOrder: e.target.value })}
              >
                <option value="">-- None --</option>
                {workOrders.map((wo) => (
                  <option key={wo._id} value={wo._id}>
                    {wo.workOrderNumber || wo.title || `WO #${wo._id.slice(-6)}`}{wo.label ? ` – ${wo.label}` : ""}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* Description / Purpose */}
            <Col xs={12}>
              <Form.Label className="fw-semibold small text-muted">PURPOSE / DESCRIPTION *</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Explain the reason for this expense..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </Col>

            {/* Receipts / Bills Upload */}
            <Col xs={12}>
              <Form.Label className="fw-semibold small text-muted">RECEIPTS & BILLS (IMAGES / PDF)</Form.Label>
              <div
                className="p-3 border rounded border-dashed text-center bg-light"
                style={{ borderStyle: "dashed", borderColor: "#cbd5e1" }}
              >
                <input
                  type="file"
                  id="claim-file-input"
                  multiple
                  accept="image/*,application/pdf"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                <label htmlFor="claim-file-input" className="btn btn-sm btn-outline-primary mb-2" style={{ cursor: "pointer" }}>
                  Browse & Upload Receipts
                </label>
                <div className="text-muted small">
                  Supports multiple PNG, JPG, WEBP, and PDF receipts (Max 10MB each)
                </div>
              </div>

              {/* Uploaded File Previews */}
              {filePreviews.length > 0 && (
                <div className="mt-3 d-flex flex-wrap gap-2">
                  {filePreviews.map((file, idx) => (
                    <div
                      key={idx}
                      className="p-2 border rounded bg-white shadow-sm d-flex align-items-center gap-2"
                      style={{ maxWidth: 220 }}
                    >
                      {file.isImage && file.url ? (
                        <img
                          src={file.url}
                          alt="preview"
                          style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 6 }}
                        />
                      ) : (
                        <div
                          className="bg-danger-subtle text-danger rounded d-flex align-items-center justify-content-center"
                          style={{ width: 44, height: 44, fontWeight: "bold", fontSize: 11 }}
                        >
                          PDF
                        </div>
                      )}
                      <div className="overflow-hidden flex-grow-1" style={{ fontSize: 11 }}>
                        <div className="text-truncate fw-semibold">{file.name}</div>
                        <div className="text-muted">{file.size}</div>
                      </div>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-danger p-0"
                        onClick={() => handleRemoveFile(idx)}
                        title="Remove file"
                      >
                        ✕
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" type="submit" disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" className="me-1" /> Submitting...
              </>
            ) : claim ? (
              "Update Claim"
            ) : (
              "Submit Expense Claim"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ReimbursementFormModal;
